import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const Auto_FullScreen = () => {
  const navigate = useNavigate();

  useEffect(() => {
    let isRequestingFullscreen = false;
    let isRedirecting = false;
    let isFilePickerOpen = false;
    let filePickerTimeout = null;

    // ==============================
    // SAFARI DETECTION
    // ==============================
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    // ==============================
    // FULLSCREEN
    // ==============================
    const requestFullScreen = async () => {
      if (
        document.fullscreenElement ||
        isRequestingFullscreen ||
        isFilePickerOpen
      ) {
        return;
      }

      // Safari needs a real user interaction
      // before fullscreen can be requested.
      if (isSafari) {
        return;
      }

      isRequestingFullscreen = true;

      try {
        await document.documentElement.requestFullscreen();

        // Lock Escape key where supported
        if (navigator.keyboard && navigator.keyboard.lock) {
          try {
            await navigator.keyboard.lock(["Escape"]);
          } catch (error) {
            console.warn("Keyboard lock is not supported:", error);
          }
        }
      } catch (error) {
        console.warn("Automatic fullscreen was blocked by browser:", error);
      } finally {
        isRequestingFullscreen = false;
      }
    };

    // ==============================
    // INITIAL FULLSCREEN
    // ==============================
    requestFullScreen();

    // ==============================
    // USER INTERACTION
    // ==============================
    const handleUserInteraction = () => {
      if (!isFilePickerOpen) {
        requestFullScreen();
      }
    };

    window.addEventListener("click", handleUserInteraction);

    window.addEventListener("touchstart", handleUserInteraction);

    // ==============================
    // FILE PICKER
    // ==============================
    const handleFilePicker = (event) => {
      const target = event.target;

      if (target && target.type === "file") {
        console.log("File picker opened");

        isFilePickerOpen = true;

        // Clear previous timeout
        if (filePickerTimeout) {
          clearTimeout(filePickerTimeout);
        }

        /*
         * Safari may trigger fullscreenchange
         * while opening the native file picker.
         *
         * Keep this flag alive long enough for
         * the native picker to open and close.
         */
        filePickerTimeout = setTimeout(() => {
          isFilePickerOpen = false;

          console.log("File picker closed");

          /*
           * Do NOT automatically request fullscreen
           * in Safari here.
           *
           * Safari requires a user gesture.
           */
          if (!isSafari) {
            requestFullScreen();
          }
        }, 2000);
      }
    };

    document.addEventListener("click", handleFilePicker, true);

    // ==============================
    // BLOCK REFRESH SHORTCUTS
    // ==============================
    const handleKeyDown = (event) => {
      // F5
      if (event.key === "F5") {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }

      // Ctrl + R
      if (event.ctrlKey && event.key.toLowerCase() === "r") {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }

      // Ctrl + Shift + R
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === "r") {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }

      // Cmd + R
      if (event.metaKey && event.key.toLowerCase() === "r") {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }

      // Cmd + Shift + R
      if (event.metaKey && event.shiftKey && event.key.toLowerCase() === "r") {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);

    // ==============================
    // DISABLE RIGHT CLICK
    // ==============================
    const handleContextMenu = (event) => {
      event.preventDefault();
    };

    document.addEventListener("contextmenu", handleContextMenu);

    // ==============================
    // FULLSCREEN CHANGE
    // ==============================
    const handleFullscreenChange = () => {
      /*
       * VERY IMPORTANT:
       *
       * Safari can leave fullscreen when opening
       * the native file picker.
       *
       * Never redirect during file upload.
       */
      if (isFilePickerOpen) {
        console.log("Ignoring fullscreen change because file picker is open");

        return;
      }

      /*
       * Safari fullscreen behavior is different.
       *
       * Don't immediately redirect after a fullscreen
       * change because Safari may temporarily leave
       * fullscreen for browser-controlled operations.
       */
      if (isSafari) {
        return;
      }

      // User manually exited fullscreen
      if (!document.fullscreenElement && !isRedirecting) {
        isRedirecting = true;

        // Unlock keyboard
        if (navigator.keyboard && navigator.keyboard.unlock) {
          try {
            navigator.keyboard.unlock();
          } catch (error) {
            console.warn("Keyboard unlock failed:", error);
          }
        }

        navigate("/", {
          replace: true,
        });
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    // ==============================
    // CLEANUP
    // ==============================
    return () => {
      window.removeEventListener("click", handleUserInteraction);

      window.removeEventListener("touchstart", handleUserInteraction);

      window.removeEventListener("keydown", handleKeyDown, true);

      document.removeEventListener("click", handleFilePicker, true);

      document.removeEventListener("contextmenu", handleContextMenu);

      document.removeEventListener("fullscreenchange", handleFullscreenChange);

      if (filePickerTimeout) {
        clearTimeout(filePickerTimeout);
      }

      if (navigator.keyboard && navigator.keyboard.unlock) {
        try {
          navigator.keyboard.unlock();
        } catch (error) {
          console.warn("Keyboard unlock failed:", error);
        }
      }
    };
  }, [navigate]);

  return null;
};

export default Auto_FullScreen;
